import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, MenuItem, IconButton, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Grid2 as Grid,
  FormControlLabel, Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation } from "@tanstack/react-query";
import { diagnosticsApi, type DxOrderType, type DxOrderPriority, type DxOrderLineInput, type DxOrderSource, type DxPatientCategory } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";

const NIL = "00000000-0000-0000-0000-000000000000";
const SOURCES: DxOrderSource[] = ["Opd", "Ipd", "Emergency", "NurseStation", "MobileApp", "PatientPortal"];
const CATEGORIES: DxPatientCategory[] = ["WalkIn", "Opd", "Ipd", "Corporate", "Insurance"];

export function NewDiagnosticOrderPage() {
  const [orderType, setOrderType] = useState<DxOrderType>("Outpatient");
  const [priority, setPriority] = useState<DxOrderPriority>("Routine");
  const [source, setSource] = useState<DxOrderSource>("Opd");
  const [patientCategory, setPatientCategory] = useState<DxPatientCategory>("WalkIn");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [isHomeCollection, setIsHomeCollection] = useState(false);
  const [collectionAddress, setCollectionAddress] = useState("");
  const [patientId, setPatientId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [deptId, setDeptId] = useState("");
  const [lines, setLines] = useState<DxOrderLineInput[]>([]);

  const depts = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });
  const tests = useQuery({ queryKey: ["dx-tests", deptId], queryFn: () => diagnosticsApi.listTests(deptId || undefined), enabled: !!deptId });

  const addTest = (id: string, name: string) => {
    if (lines.some(l => l.catalogRefId === id)) return;
    setLines([...lines, { kind: "Lab", catalogRefId: id, displayName: name }]);
  };

  const create = useMutation({
    meta: { successMessage: "Order placed — invoice raised (bill-first)" },
    mutationFn: () => diagnosticsApi.createOrder({
      orderType, priority, source, patientCategory,
      clinicalNotes: clinicalNotes.trim() || null,
      diagnosis: diagnosis.trim() || null,
      isHomeCollection,
      collectionAddress: isHomeCollection ? (collectionAddress.trim() || null) : null,
      patientId: patientId.trim() || NIL,
      orderingProviderId: providerId.trim() || NIL,
      lines,
    }),
    onSuccess: () => { setLines([]); setPatientId(""); setProviderId(""); setClinicalNotes(""); setDiagnosis(""); setIsHomeCollection(false); setCollectionAddress(""); },
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · New Order</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Order details</Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField select label="Order type" size="small" fullWidth value={orderType} onChange={e => setOrderType(e.target.value as DxOrderType)}>
                  <MenuItem value="Outpatient">Outpatient</MenuItem>
                  <MenuItem value="Inpatient">Inpatient</MenuItem>
                  <MenuItem value="Emergency">Emergency</MenuItem>
                </TextField>
                <TextField select label="Priority" size="small" fullWidth value={priority} onChange={e => setPriority(e.target.value as DxOrderPriority)}>
                  <MenuItem value="Routine">Routine</MenuItem>
                  <MenuItem value="Urgent">Urgent</MenuItem>
                  <MenuItem value="Stat">Stat</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField select label="Source" size="small" fullWidth value={source} onChange={e => setSource(e.target.value as DxOrderSource)}>
                  {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField select label="Patient category" size="small" fullWidth value={patientCategory} onChange={e => setPatientCategory(e.target.value as DxPatientCategory)}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField label="Patient Id (GUID)" size="small" value={patientId} onChange={e => setPatientId(e.target.value)} placeholder={NIL} />
              <TextField label="Ordering provider Id (GUID)" size="small" value={providerId} onChange={e => setProviderId(e.target.value)} placeholder={NIL} />
              <TextField label="Clinical notes" size="small" multiline minRows={2} value={clinicalNotes} onChange={e => setClinicalNotes(e.target.value)} />
              <TextField label="Provisional diagnosis" size="small" value={diagnosis} onChange={e => setDiagnosis(e.target.value)} />
              <FormControlLabel control={<Switch checked={isHomeCollection} onChange={e => setIsHomeCollection(e.target.checked)} />} label="Home collection" />
              {isHomeCollection && <TextField label="Collection address" size="small" multiline minRows={2} value={collectionAddress} onChange={e => setCollectionAddress(e.target.value)} />}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Add tests</Typography>
            <TextField select label="Department" size="small" fullWidth value={deptId} onChange={e => setDeptId(e.target.value)} sx={{ mb: 1 }}>
              {(depts.data ?? []).map(d => <MenuItem key={d.id} value={d.id}>{d.code} — {d.name}</MenuItem>)}
            </TextField>
            <Stack spacing={0.5} sx={{ maxHeight: 220, overflow: "auto" }}>
              {(tests.data ?? []).map(t => (
                <Stack key={t.id} direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">{t.code} — {t.name}</Typography>
                  <IconButton size="small" onClick={() => addTest(t.id, t.name)}><AddIcon fontSize="small" /></IconButton>
                </Stack>
              ))}
              {deptId && (tests.data?.length ?? 0) === 0 && <Typography variant="caption" color="text.secondary">No tests in this department.</Typography>}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Order lines</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Kind</TableCell><TableCell>Test</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {lines.map((l, i) => (
                  <TableRow key={l.catalogRefId}>
                    <TableCell>{l.kind}</TableCell>
                    <TableCell>{l.displayName}</TableCell>
                    <TableCell align="right"><IconButton size="small" onClick={() => setLines(lines.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
                {lines.length === 0 && <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.secondary">Add tests from the left.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
            {create.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(create.error)}</Alert>}
            {create.isSuccess && <Alert severity="success" sx={{ mt: 2 }}>Order placed and billed via the shared sales invoice.</Alert>}
            <Button variant="contained" sx={{ mt: 2 }} disabled={lines.length === 0 || create.isPending}
              startIcon={create.isPending ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={() => create.mutate()}>Place order</Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
